/* Dutchies · kop-rewards-bar (v3)
 *
 * Behaviour for the Kopstorm multi step rewards bar rendered by snippets/kop-cart-rewards-bar.liquid.
 * Loaded once from layout/theme.liquid so the custom element definition and the gift controllers survive
 * the cart drawer replacing its full HTML on every cart:change.
 *
 * Contexts:
 *  - drawer: the snippet server renders the full bar; this element only updates fill, nodes, labels and
 *    headline in place when the cart changes (and the drawer re-render replaces it with fresh server HTML anyway).
 *  - product: the PDP block renders an empty shell; on connect it clones markup and configuration from the
 *    drawer instance (the drawer is part of the overlay group on every page). On every cart:change it updates
 *    the bar immediately and then re-clones the drawer markup once the drawer has re-rendered, so the gift
 *    chooser stays in sync as well.
 *
 * Automatic gifts: tiers with exactly one gift option and auto add enabled get their gift variant added through
 * cart/add.js when the threshold is reached and removed again below it, using the theme's bundled sections flow
 * so the drawer updates without a reload. If the shopper removes the gift manually while still qualifying, that
 * choice is respected for the session.
 *
 * Gift choice: tiers with two or more gift options render a chooser (server side, in the drawer HTML). A delegated
 * click listener swaps the chosen gift line in the cart. Gift lines carry the hidden line item property
 * _kop_reward_gift and never count toward the qualifying total.
 *
 * One gift per cart: the quantity stepper is locked on gift lines in snippets/line-item.liquid, and this file adds
 * the safety net behind it. On every cart change any line carrying _kop_reward_gift is put back to one, a second
 * gift line for the same tier is removed, and, while data-gift-one-per-cart is on, a gift product added again as a
 * normal line (Shopify keeps it separate because the properties differ) is capped at one as well. Without this a
 * zero priced gift such as the magazine can be stacked indefinitely, since there is no discount to run out.
 */
(function () {
  'use strict';

  if (window.customElements.get('kop-rewards-bar')) {
    return;
  }

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var STORAGE_KEY = 'kop-rewards-bar:dismissed';

  var formatter;
  try {
    formatter = new Intl.NumberFormat(Shopify.locale, { style: 'currency', currency: Shopify.currency.active });
  } catch (e) {
    formatter = { format: function (value) { return value.toFixed(2); } };
  }

  function kopEscape(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function getDrawerBar() {
    return document.querySelector('kop-rewards-bar[data-context="drawer"]');
  }

  /* Shopify.currency.rate is the multiplier from the store currency to the shopper's currency, the same one
     Shopify applies to the cart. Liquid cannot read it, so tiers flagged r:1 arrive in the store currency and
     are converted here. Returns 1 when the rate is unknown (primary market or Shopify object missing). */
  function currencyRate() {
    var rate = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    return rate > 0 ? rate : 1;
  }

  function parseTiers(element) {
    if (!element) { return []; }
    try {
      var tiers = JSON.parse(element.getAttribute('data-tiers') || '[]');
      var rate = currencyRate();
      tiers.forEach(function (tier) {
        if (!Array.isArray(tier.g)) { tier.g = []; }
        if (tier.r && !tier.converted) {
          tier.a = Math.round(tier.a * rate);
          tier.converted = true;
        }
      });
      tiers.sort(function (a, b) { return a.a - b.a; });
      return tiers;
    } catch (e) {
      return [];
    }
  }

  function computeTotal(cart) {
    var subtotal = (cart.items || [])
      .filter(function (item) {
        if (item.properties && item.properties._kop_reward_gift) { return false; }
        return item.requires_shipping !== false;
      })
      .reduce(function (sum, item) { return sum + (item.final_line_price || 0); }, 0);
    var discount = (cart.cart_level_discount_applications || [])
      .reduce(function (sum, d) { return sum + (d.total_allocated_amount || 0); }, 0);
    return Math.max(subtotal - discount, 0);
  }

  function giftLinesFromCart(cart) {
    var lines = {};
    (cart.items || []).forEach(function (item) {
      if (item.properties && item.properties._kop_reward_gift) {
        lines[item.properties._kop_reward_gift] = { key: item.key, variantId: item.variant_id };
      }
    });
    return lines;
  }

  function giftOnePerCartEnabled() {
    var bar = getDrawerBar();
    return !bar || bar.getAttribute('data-gift-one-per-cart') !== 'false';
  }

  /* Cart changes needed to bring every reward gift back to a single unit. Returns [{ key, quantity }]. */
  function giftClampActions(cart, tiers) {
    var items = cart.items || [];
    var actions = [];
    var seenTier = {};

    items.forEach(function (item) {
      var tierId = item.properties && item.properties._kop_reward_gift;
      if (!tierId) { return; }

      if (seenTier[tierId]) {
        /* A second gift line for the same tier can only be ours, so it goes entirely. */
        actions.push({ key: item.key, quantity: 0 });
        return;
      }

      seenTier[tierId] = true;
      if (item.quantity > 1) { actions.push({ key: item.key, quantity: 1 }); }
    });

    if (!giftOnePerCartEnabled()) { return actions; }

    /* A gift product added through a normal add to cart has no property, so it needs the variant check.
       It is capped rather than removed: the shopper keeps the line they chose to add. */
    var giftVariants = {};
    (tiers || []).forEach(function (tier) {
      (tier.g || []).forEach(function (gift) { giftVariants[gift.v] = true; });
    });

    items.forEach(function (item) {
      if (item.properties && item.properties._kop_reward_gift) { return; }
      if (!giftVariants[item.variant_id]) { return; }
      if (item.quantity > 1) { actions.push({ key: item.key, quantity: 1 }); }
    });

    return actions;
  }

  function bundleSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections.join(',');
  }

  /* ------------------------------------------------------------------ element */

  class KopRewardsBar extends HTMLElement {
    connectedCallback() {
      this.onCartChange = this.handleCartChange.bind(this);

      if (this.getAttribute('data-context') === 'product') {
        this.initProduct();
        return;
      }

      this.tiers = parseTiers(this);
      this.tmplRemaining = this.getAttribute('data-tmpl-remaining') || '';
      this.tmplComplete = this.getAttribute('data-tmpl-complete') || '';
      document.addEventListener('cart:change', this.onCartChange);
      this.render(parseInt(this.getAttribute('data-total'), 10) || 0);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this.onCartChange);
      if (this.onCartRefresh) {
        document.removeEventListener('cart:refresh', this.onCartRefresh);
      }
      if (this.syncTimer) {
        window.clearTimeout(this.syncTimer);
      }
    }

    syncFromDrawer() {
      var drawerBar = getDrawerBar();
      if (!drawerBar) {
        this.hidden = true;
        return false;
      }
      this.innerHTML = drawerBar.innerHTML;
      this.style.setProperty('--kop-rewards-color', drawerBar.style.getPropertyValue('--kop-rewards-color'));
      this.tiers = parseTiers(drawerBar);
      this.tmplRemaining = drawerBar.getAttribute('data-tmpl-remaining') || '';
      this.tmplComplete = drawerBar.getAttribute('data-tmpl-complete') || '';
      this.setAttribute('data-tiers', drawerBar.getAttribute('data-tiers') || '[]');
      this.hidden = this.tiers.length === 0;
      return true;
    }

    initProduct() {
      var element = this;
      var initialize = function () {
        if (!element.syncFromDrawer()) { return; }

        document.addEventListener('cart:change', element.onCartChange);
        element.onCartRefresh = function () {
          fetch(ROOT + 'cart.js')
            .then(function (response) { return response.json(); })
            .then(function (cart) {
              element.render(computeTotal(cart));
              element.scheduleSync();
            })
            .catch(function () {});
        };
        document.addEventListener('cart:refresh', element.onCartRefresh);

        var drawerBar = getDrawerBar();
        element.render(parseInt(drawerBar.getAttribute('data-total'), 10) || 0);
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize, { once: true });
      } else {
        initialize();
      }
    }

    scheduleSync() {
      /* The theme replaces the drawer HTML on the same cart:change event; wait a beat so the
         re-clone picks up the fresh server markup, including the gift chooser state. */
      var element = this;
      if (this.syncTimer) { window.clearTimeout(this.syncTimer); }
      this.syncTimer = window.setTimeout(function () {
        element.syncTimer = null;
        element.syncFromDrawer();
      }, 150);
    }

    handleCartChange(event) {
      var cart = event.detail && event.detail.cart;
      if (!cart || !cart.items) { return; }
      this.render(computeTotal(cart));
      if (this.getAttribute('data-context') === 'product') {
        this.scheduleSync();
      }
    }

    render(total) {
      var tiers = this.tiers || [];
      var count = tiers.length;
      if (!count) { return; }

      var reached = 0;
      for (var i = 0; i < count; i++) {
        if (total >= tiers[i].a) { reached++; }
      }

      var fill;
      if (reached >= count) {
        fill = 100;
      } else {
        var startPos = reached > 0 ? tiers[reached - 1].p : 0;
        var startAmt = reached > 0 ? tiers[reached - 1].a : 0;
        var endPos = tiers[reached].p;
        var endAmt = tiers[reached].a;
        var span = endAmt - startAmt;
        fill = span > 0 ? startPos + ((total - startAmt) * (endPos - startPos)) / span : startPos;
        if (fill < 0) { fill = 0; }
      }

      var fillEl = this.querySelector('[data-kop-fill]');
      if (fillEl) { fillEl.style.width = fill + '%'; }

      /* Nodes and labels are server rendered in the same ascending order as the tiers, so the converted
         amount is read from the tier at the same index rather than from the (store currency) attribute. */
      this.querySelectorAll('[data-kop-node]').forEach(function (node, index) {
        var amount = tiers[index] ? tiers[index].a : parseInt(node.getAttribute('data-kop-node'), 10);
        node.setAttribute('data-kop-node', amount);
        node.classList.toggle('is-reached', total >= amount);
      });
      this.querySelectorAll('[data-kop-label]').forEach(function (label, index) {
        var amount = tiers[index] ? tiers[index].a : parseInt(label.getAttribute('data-kop-label'), 10);
        label.setAttribute('data-kop-label', amount);
        label.classList.toggle('is-reached', total >= amount);
      });

      /* Gift choosers for tiers that needed the rate are rendered hidden by Liquid; show or hide every
         chooser from the converted threshold so the drawer and the product page instance agree. */
      var byId = {};
      tiers.forEach(function (tier) { byId[tier.id] = tier; });
      this.querySelectorAll('[data-kop-gift-group]').forEach(function (group) {
        var tier = byId[group.getAttribute('data-kop-gift-group')];
        var amount = tier ? tier.a : parseInt(group.getAttribute('data-kop-gift-amount'), 10);
        group.hidden = !(total >= amount);
      });

      var headlineEl = this.querySelector('[data-kop-headline]');
      if (headlineEl) {
        if (reached >= count) {
          // innerHTML on purpose: the copy comes from the theme settings and may contain <strong>,
          // exactly like the server rendered headline in kop-cart-rewards-bar.liquid.
          headlineEl.innerHTML = this.tmplComplete;
        } else {
          var next = tiers[reached];
          var remaining = formatter.format(Math.max(next.a - total, 0) / 100);
          headlineEl.innerHTML = this.tmplRemaining
            .replace('{amount}', kopEscape(remaining))
            .replace('{reward}', kopEscape(next.l || ''));
        }
      }

      this.classList.remove('is-awaiting-rate');
    }
  }

  window.customElements.define('kop-rewards-bar', KopRewardsBar);

  /* ------------------------------------------------------- gift cart helpers */

  var giftBusy = false;
  var giftPending = null;
  var giftFailed = {};
  var lastPresence = {};
  var dismissed = {};

  try {
    dismissed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch (e) {
    dismissed = {};
  }

  function saveDismissed() {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
    } catch (e) {
      /* storage unavailable, dismissal only lives for this page */
    }
  }

  function dispatchCartSync(lastSections, didAdd) {
    return fetch(ROOT + 'cart.js')
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        if (lastSections) {
          cart.sections = lastSections;
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: {
                baseEvent: didAdd ? 'variant:add' : 'kop:gift-sync',
                cart: cart
              }
            })
          );
        } else {
          /* No bundled section HTML came back; a refresh keeps the drawer consistent without a broken replace. */
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      });
  }

  function releaseGiftBusy() {
    giftBusy = false;
    if (giftPending) {
      var pending = giftPending;
      giftPending = null;
      evaluateGifts(pending.total, pending.giftLines, pending.clamps);
    }
  }

  /* ------------------------------------------------- automatic gift controller */

  function evaluateGifts(total, giftLines, clamps) {
    if (window.Shopify && window.Shopify.designMode) { return; }

    var tiers = parseTiers(getDrawerBar());
    if (!tiers.length) { return; }

    if (giftBusy) {
      giftPending = { total: total, giftLines: giftLines, clamps: clamps };
      return;
    }

    var toAdd = [];
    var toRemove = [];
    var toClamp = clamps || [];

    tiers.forEach(function (tier) {
      var single = tier.g.length === 1 ? tier.g[0] : null;
      var hasGifts = tier.g.length > 0;
      if (!hasGifts) { return; }

      var isReached = total >= tier.a;
      var line = giftLines[tier.id];
      var present = Boolean(line);

      if (isReached && !present && single && tier.aa) {
        if (lastPresence[tier.id] === true) {
          /* The shopper removed the gift while still qualifying: respect that for this session. */
          dismissed[tier.id] = true;
          saveDismissed();
        } else if (!dismissed[tier.id] && !giftFailed[tier.id] && single.av) {
          toAdd.push({ tier: tier, variantId: single.v });
        }
      }

      if (!isReached) {
        if (dismissed[tier.id]) {
          delete dismissed[tier.id];
          saveDismissed();
        }
        if (present && tier.ar) {
          toRemove.push(line.key);
        }
      }

      lastPresence[tier.id] = present;
    });

    if (!toAdd.length && !toRemove.length && !toClamp.length) { return; }

    giftBusy = true;

    var sections = bundleSections();
    var lastSections = null;
    var didAdd = false;
    var queue = Promise.resolve();

    toAdd.forEach(function (entry) {
      queue = queue.then(function () {
        var properties = {};
        properties._kop_reward_gift = entry.tier.id;
        return fetch(ROOT + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [{ id: entry.variantId, quantity: 1, properties: properties }],
            sections: sections
          })
        }).then(function (response) {
          if (!response.ok) {
            giftFailed[entry.tier.id] = true;
            return null;
          }
          didAdd = true;
          lastPresence[entry.tier.id] = true;
          return response.json().then(function (json) {
            lastSections = json.sections || lastSections;
          });
        });
      });
    });

    toClamp.forEach(function (entry) {
      queue = queue.then(function () {
        return fetch(ROOT + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: entry.key, quantity: entry.quantity, sections: sections })
        }).then(function (response) {
          if (response.ok) {
            return response.json().then(function (json) {
              lastSections = json.sections || lastSections;
            });
          }
          return null;
        });
      });
    });

    toRemove.forEach(function (lineKey) {
      queue = queue.then(function () {
        return fetch(ROOT + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: lineKey, quantity: 0, sections: sections })
        }).then(function (response) {
          if (response.ok) {
            return response.json().then(function (json) {
              lastSections = json.sections || lastSections;
            });
          }
          return null;
        });
      });
    });

    queue
      .then(function () { return dispatchCartSync(lastSections, didAdd); })
      .catch(function () {})
      .then(releaseGiftBusy);
  }

  /* --------------------------------------------------- gift choice controller */

  function chooseGift(tierId, variantId, button) {
    if (giftBusy) { return; }
    giftBusy = true;

    var group = button.closest('[data-kop-gift-group]');
    if (group) { group.setAttribute('aria-busy', 'true'); }

    var sections = bundleSections();
    var lastSections = null;

    fetch(ROOT + 'cart.js')
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var line = giftLinesFromCart(cart)[tierId];

        if (line && line.variantId === variantId) {
          /* This gift is already in the cart, nothing to do. */
          return null;
        }

        var removal = Promise.resolve();
        if (line) {
          removal = fetch(ROOT + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          });
        }

        return removal.then(function () {
          var properties = {};
          properties._kop_reward_gift = tierId;
          return fetch(ROOT + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1, properties: properties }],
              sections: sections
            })
          });
        }).then(function (response) {
          if (!response.ok) { return null; }
          lastPresence[tierId] = true;
          if (dismissed[tierId]) {
            delete dismissed[tierId];
            saveDismissed();
          }
          return response.json().then(function (json) {
            lastSections = json.sections || lastSections;
          });
        }).then(function () {
          return dispatchCartSync(lastSections, true);
        });
      })
      .catch(function () {})
      .then(function () {
        if (group && group.isConnected) { group.removeAttribute('aria-busy'); }
        releaseGiftBusy();
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-kop-gift-choice]');
    if (!button || button.disabled) { return; }
    event.preventDefault();
    var tierId = button.getAttribute('data-tier');
    var variantId = parseInt(button.getAttribute('data-variant'), 10);
    if (!tierId || !variantId) { return; }
    chooseGift(tierId, variantId, button);
  });

  /* ------------------------------------------------------------- initial pass */

  function initialEvaluation() {
    var drawerBar = getDrawerBar();
    var tiers = parseTiers(drawerBar);
    if (!tiers.length) { return; }

    /* Presence and line keys were computed server side, so the initial pass needs no extra request. */
    var giftLines = {};
    var clamps = [];
    tiers.forEach(function (tier) {
      if (tier.k) {
        giftLines[tier.id] = { key: tier.k, variantId: tier.kv || 0 };
        if ((tier.kq || 0) > 1) { clamps.push({ key: tier.k, quantity: 1 }); }
      }
      lastPresence[tier.id] = Boolean(tier.k);
    });

    evaluateGifts(parseInt(drawerBar.getAttribute('data-total'), 10) || 0, giftLines, clamps);
  }

  document.addEventListener('cart:change', function (event) {
    if (event.detail && event.detail.cart && event.detail.cart.items) {
      var cart = event.detail.cart;
      evaluateGifts(
        computeTotal(cart),
        giftLinesFromCart(cart),
        giftClampActions(cart, parseTiers(getDrawerBar()))
      );
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialEvaluation, { once: true });
  } else {
    initialEvaluation();
  }
})();
